import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  addUserMessage,
  startAssistantMessage,
  beginSending,
  openChatStream,
  cancelStream,
} from "../../redux/chat/chatSlice";
import wwllogo from "../../assets/icons/wwllogo.webp";
import { hasChatAccess } from "../../redux/user/userSlice";

const FloatingChatWidget = () => {
  const dispatch = useDispatch();
  const location = useLocation();

  const messages = useSelector((state) => state.chat.messages);
  const sending = useSelector((state) => state.chat.sending);
  const hasAssistantPermission = useSelector(hasChatAccess);

  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState("");

  const endRef = useRef(null);
  const inputRef = useRef(null);
  const isInputFocusedRef = useRef(false);

  const isOnFullAssistantPage = location.pathname === "/ai-assistant";

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sending, isOpen]);

  useEffect(() => {
    if (isOnFullAssistantPage) {
      setIsOpen(false);
    }
  }, [isOnFullAssistantPage]);

  /**
   * Strong mobile scroll lock.
   *
   * This prevents the page behind the floating widget from scrolling/panning
   * while the widget is open. This is especially important on mobile Safari,
   * where a focused input can cause the visual viewport/caret to drift while
   * the page behind the fixed widget is still scrollable.
   */
  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY || window.pageYOffset;

    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevHtmlOverflowX = document.documentElement.style.overflowX;
    const prevHtmlOverflowY = document.documentElement.style.overflowY;

    const prevBodyOverflow = document.body.style.overflow;
    const prevBodyOverflowX = document.body.style.overflowX;
    const prevBodyOverflowY = document.body.style.overflowY;
    const prevBodyPosition = document.body.style.position;
    const prevBodyTop = document.body.style.top;
    const prevBodyLeft = document.body.style.left;
    const prevBodyRight = document.body.style.right;
    const prevBodyWidth = document.body.style.width;
    const prevBodyTouchAction = document.body.style.touchAction;

    document.documentElement.style.overflow = "hidden";
    document.documentElement.style.overflowX = "hidden";
    document.documentElement.style.overflowY = "hidden";

    document.body.style.overflow = "hidden";
    document.body.style.overflowX = "hidden";
    document.body.style.overflowY = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";
    document.body.style.touchAction = "none";

    return () => {
      document.documentElement.style.overflow = prevHtmlOverflow;
      document.documentElement.style.overflowX = prevHtmlOverflowX;
      document.documentElement.style.overflowY = prevHtmlOverflowY;

      document.body.style.overflow = prevBodyOverflow;
      document.body.style.overflowX = prevBodyOverflowX;
      document.body.style.overflowY = prevBodyOverflowY;
      document.body.style.position = prevBodyPosition;
      document.body.style.top = prevBodyTop;
      document.body.style.left = prevBodyLeft;
      document.body.style.right = prevBodyRight;
      document.body.style.width = prevBodyWidth;
      document.body.style.touchAction = prevBodyTouchAction;

      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  const handleSend = (e) => {
    e.preventDefault();

    const trimmed = input.trim();

    if (!trimmed || sending) return;

    const conversationForServer = [
      ...messages.map((message) => ({
        role: message.role,
        content: message.text || "",
      })),
      {
        role: "user",
        content: trimmed,
      },
    ];

    dispatch(addUserMessage(trimmed));
    dispatch(startAssistantMessage());
    dispatch(beginSending());
    dispatch(openChatStream(conversationForServer));

    setInput("");
  };

  const handleStop = () => {
    dispatch(cancelStream());
  };

  if (isOnFullAssistantPage) {
    return null;
  }

  return (
    <div
      className={
        isOpen
          ? "fixed inset-0 z-[9999] flex flex-col justify-end overflow-hidden bg-transparent p-4 md:inset-auto md:bottom-5 md:right-5 md:items-end md:overflow-visible md:p-0"
          : "fixed bottom-5 right-5 z-[9999] flex flex-col items-end"
      }
      onTouchMove={(e) => {
        if (!isOpen) return;

        const scrollArea = e.target.closest("[data-floating-chat-scroll]");
        const composerArea = e.target.closest("[data-floating-chat-composer]");

        /**
         * Only the messages area should scroll.
         * The composer/input area should never be treated as a scroll surface,
         * because that is what can make the focused blue caret line drift upward.
         */
        if (!scrollArea || composerArea) {
          e.preventDefault();
        }
      }}
    >
      {/* Chat Window */}
      {isOpen && (
        <div className="mb-4 flex h-[calc(100dvh-120px)] w-full max-w-none flex-col overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-2xl md:h-[560px] md:w-[380px] md:max-w-[calc(100vw-32px)]">
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between bg-wwlOrange px-4 py-3 text-white">
            <div>
              <p className="text-sm font-semibold leading-tight">
                Workweek Lunch GPT
              </p>
              <p className="text-xs text-white/90">
                Ask about recipes, meal plans, and more
              </p>
            </div>

            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-lg transition hover:bg-white/30"
              aria-label="Close chat"
            >
              ×
            </button>
          </div>

          {/* Full Page Link */}
          <div className="shrink-0 border-b border-gray-100 bg-orange-50 px-4 py-2">
            <Link
              to="/ai-assistant"
              className="text-sm font-medium text-wwlOrange underline underline-offset-2 hover:opacity-80"
            >
              Open full chatbot page →
            </Link>
          </div>

          {/* Messages */}
          <div
            data-floating-chat-scroll
            onTouchStart={() => {
              /**
               * Fallback for mobile:
               * If the user starts scrolling the messages while the input is focused,
               * blur the input so the mobile caret/keyboard cannot visually drift.
               */
              if (document.activeElement === inputRef.current) {
                inputRef.current.blur();
              }
            }}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-gray-50 px-3 py-4"
            style={{ WebkitOverflowScrolling: "touch" }}
          >
            {messages.length === 0 && (
              <div className="rounded-2xl border border-orange-100 bg-white p-4 shadow-sm">
                <p className="text-sm font-semibold text-gray-900">
                  Hi! How can I help?
                </p>
                <p className="mt-1 text-sm text-gray-600">
                  Ask me to find recipes, suggest meal plans, help with shopping
                  lists, or answer account questions.
                </p>
              </div>
            )}

            <div className="space-y-4">
              {messages.map((message) => {
                const isUser = message.role === "user";

                return (
                  <div
                    key={message.id}
                    className={`flex items-end gap-2 ${
                      isUser ? "justify-end" : "justify-start"
                    }`}
                  >
                    {!isUser && (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full">
                        <img
                          src={wwllogo}
                          alt="WWL Logo"
                          className="h-full w-full rounded-full object-cover scale-[1.2]"
                        />
                      </div>
                    )}

                    <div
                      className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                        isUser
                          ? "rounded-br-md bg-wwlOrange text-white"
                          : "rounded-bl-md border border-gray-100 bg-white text-gray-800"
                      }`}
                    >
                      {isUser ? (
                        <p className="whitespace-pre-wrap">{message.text}</p>
                      ) : message.text ? (
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ children }) => (
                              <p className="mb-2 last:mb-0">{children}</p>
                            ),
                            ul: ({ children }) => (
                              <ul className="mb-2 list-disc pl-5 last:mb-0">
                                {children}
                              </ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="mb-2 list-decimal pl-5 last:mb-0">
                                {children}
                              </ol>
                            ),
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                className="font-medium text-wwlOrange underline underline-offset-2 hover:opacity-80"
                              >
                                {children}
                              </a>
                            ),
                          }}
                        >
                          {message.text}
                        </ReactMarkdown>
                      ) : (
                        <div
                          className="flex items-center gap-2 py-2 pr-2"
                          aria-label="Assistant is typing"
                        >
                          <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:-0.2s]" />
                          <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400" />
                          <span className="block h-1.5 w-1.5 animate-bounce rounded-full bg-zinc-400 [animation-delay:0.2s]" />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              <div ref={endRef} />
            </div>
          </div>

          {/* Composer */}
          {hasAssistantPermission ? (
            <form
              data-floating-chat-composer
              onSubmit={handleSend}
              onTouchMove={(e) => e.preventDefault()}
              className="shrink-0 border-t border-gray-200 bg-white p-3"
            >
              <div className="flex items-center gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onFocus={() => {
                    isInputFocusedRef.current = true;
                  }}
                  onBlur={() => {
                    isInputFocusedRef.current = false;
                  }}
                  autoComplete="off"
                  placeholder="Ask something..."
                  aria-label="Message"
                  className="
                    h-11
                    min-w-0
                    flex-1
                    rounded-2xl
                    border border-gray-300
                    bg-white
                    px-4
                    text-[16px] md:text-sm
                    leading-[44px]
                    text-gray-900
                    outline-none
                    placeholder:text-gray-500
                    transition
                    focus:border-wwlOrange
                    focus:ring-2
                    focus:ring-orange-100
                  "
                />

                {sending ? (
                  <button
                    type="button"
                    onClick={handleStop}
                    className="h-11 shrink-0 rounded-2xl bg-gray-800 px-4 text-[16px] font-semibold text-white transition hover:bg-gray-900 md:text-sm"
                  >
                    Stop
                  </button>
                ) : (
                  <button
                    type="submit"
                    disabled={!input.trim()}
                    className="h-11 shrink-0 rounded-2xl bg-wwlOrange px-4 text-[16px] font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  >
                    Send
                  </button>
                )}
              </div>
            </form>
          ) : (
            <div className="shrink-0 border-t border-gray-200 bg-white p-4">
              <p className="text-sm text-gray-700">
                This assistant is available with paid membership access.
              </p>

              <Link
                to="/ai-assistant"
                className="mt-2 inline-block text-sm font-semibold text-wwlOrange underline underline-offset-2 hover:opacity-80"
              >
                View chatbot access →
              </Link>
            </div>
          )}
        </div>
      )}

      {/* Floating Button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex h-16 w-16 items-center justify-center rounded-full bg-wwlOrange text-white shadow-2xl transition hover:scale-105 hover:opacity-95"
        aria-label={isOpen ? "Close chatbot" : "Open chatbot"}
      >
        {isOpen ? (
          <span className="text-3xl leading-none">×</span>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 4v-4z"
            />
          </svg>
        )}
      </button>
    </div>
  );
};

export default FloatingChatWidget;